import type { ErrorEvent, StackFrame } from '@sentry/sveltekit';

// Functions that only exist in scripts other software injects into our pages:
// - dispatchToBridge, sendPostMessage: Instagram/Facebook's iOS in-app browser
//   reports login fields to the app through these, throwing InvalidAccessError.
// - addEL_hook: a scraper ("pc" user agent, a `[jshost] eval` console line)
//   wraps addEventListener and throws on Sentry's own listeners.
const INJECTED_FUNCTIONS = new Set([
	'dispatchToBridge',
	'sendPostMessage',
	'addEL_hook',
]);

// Chrome (CriOS) and the Google app (GSA) on iOS inject Google Translate.
const GOOGLE_IOS_APP = /\b(CriOS|GSA)\//;

// Google Translate's minified error, thrown with no stack at times.
const GOOGLE_TRANSLATE_MESSAGE = 'La';

// Android apps' web views mark their user agent with `; wv)`.
const ANDROID_WEBVIEW = /; wv\)/;

// An injected script runs under the URL of the page it was injected into, which
// stays the landing page after client-side navigation, so any page of the site
// counts. Our own code always runs from the /_app/ bundle.
const isOnAPage = (frame: StackFrame, origin: string) => {
	if (!frame.filename || frame.filename === 'undefined') {
		return true;
	}

	try {
		const url = new URL(frame.filename);

		return url.origin === origin && !url.pathname.startsWith('/_app/');
	} catch {
		return false;
	}
};

/**
 * Whether an error comes from a script a browser or in-app browser injected
 * into the page. These run under a page URL, so denyUrls can't tell them apart
 * from our inline scripts: match on the stack shape instead.
 */
export const isInjectedScriptError = (
	event: ErrorEvent,
	userAgent: string,
	pageUrl: string
) => {
	const values = event.exception?.values ?? [];
	const frames = values.flatMap((value) => value.stacktrace?.frames ?? []);

	if (frames.some((frame) => INJECTED_FUNCTIONS.has(frame.function ?? ''))) {
		return true;
	}

	const { origin } = new URL(pageUrl);

	// Android app web views inject scripts that fail to parse, reported at line 1
	// of the page. Line 1 of our HTML is the doctype and our inline scripts sit
	// further down, so their errors never report it.
	if (
		ANDROID_WEBVIEW.test(userAgent) &&
		values.some((value) => value.type === 'SyntaxError') &&
		frames.length > 0 &&
		frames.every((frame) => frame.lineno === 1 && isOnAPage(frame, origin))
	) {
		return true;
	}

	if (!GOOGLE_IOS_APP.test(userAgent)) {
		return false;
	}

	if (values.some((value) => value.value === GOOGLE_TRANSLATE_MESSAGE)) {
		return true;
	}

	// Google Translate ("Maximum call stack size exceeded"): none of the frames
	// are in our bundle, all of them are on a page of the site.
	return frames.length > 0 && frames.every((frame) => isOnAPage(frame, origin));
};
